/**
 * Language Switcher — compact flag toggle for EN/DE
 * Uses inline SVG flags (emoji flags don't render on Windows)
 */

import React from 'react';
import { useLanguageStore, type Language } from '@/stores/languageStore';

/** UK flag - simplified SVG */
function FlagGB() {
  return (
    <svg width="24" height="16" viewBox="0 0 60 40" style={{ borderRadius: 3, display: 'block' }}>
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="7" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

/** German flag - simplified SVG */
function FlagDE() {
  return (
    <svg width="24" height="16" viewBox="0 0 60 40" style={{ borderRadius: 3, display: 'block' }}>
      <rect width="60" height="13.33" y="0" fill="#000" />
      <rect width="60" height="13.34" y="13.33" fill="#DD0000" />
      <rect width="60" height="13.33" y="26.67" fill="#FFCC00" />
    </svg>
  );
}

const flags: { lang: Language; Flag: () => React.ReactNode; label: string }[] = [
  { lang: 'en', Flag: FlagGB, label: 'English' },
  { lang: 'de', Flag: FlagDE, label: 'Deutsch' },
];

/**
 * Clear any leftover Google Translate state when the user picks an EN/DE flag.
 *
 * We do NOT drive Google Translate to translate the page — native i18n covers
 * every UI string (en.ts / de.ts, full key parity) and user content is
 * localized via useLocalized(). Letting Google Translate also rewrite the DOM
 * causes React's reconciler to lose its grip on controlled inputs (e.g. the
 * search/filter bar's <input>s) and the bar gets destroyed on next render.
 *
 * If a previous session had Google Translate active, clear the cookie so it
 * stops fighting React on subsequent loads.
 */
function clearGoogleTranslate() {
  const host = window.location.hostname;
  const parentDomain = (() => {
    const parts = host.split('.');
    return parts.length > 1 ? '.' + parts.slice(-2).join('.') : null;
  })();
  const clearCookie = (domain?: string) => {
    const dom = domain ? `; domain=${domain}` : '';
    document.cookie = `googtrans=; path=/${dom}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  };
  clearCookie();
  if (parentDomain) clearCookie(parentDomain);
  // If Google Translate has already mutated the DOM in this session, reload
  // once so React renders into clean markup.
  const currentlyTranslated = document.documentElement.lang && document.documentElement.lang !== 'en';
  if (currentlyTranslated) setTimeout(() => window.location.reload(), 50);
}

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguageStore();

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 8, marginRight: 8 }}>
      {flags.map(({ lang, Flag, label }) => (
        <button
          key={lang}
          onClick={() => { setLanguage(lang); clearGoogleTranslate(); }}
          title={label}
          style={{
            background: language === lang ? 'rgba(255,255,255,0.12)' : 'none',
            border: language === lang ? '2px solid rgba(245,166,35,0.6)' : '2px solid transparent',
            cursor: 'pointer',
            padding: '3px 4px',
            borderRadius: 5,
            opacity: language === lang ? 1 : 0.5,
            transition: 'opacity 0.15s, border-color 0.15s, background 0.15s',
            lineHeight: 0,
            display: 'flex',
            alignItems: 'center',
          }}
          aria-label={label}
        >
          <Flag />
        </button>
      ))}
    </div>
  );
}
